import React, { useMemo } from 'react';

// Indented <select> for picking a functional location on the asset form.
// Accepts the flat array returned by GET /api/functional-locations and
// renders it as a depth-indented dropdown.
//
// Props:
//   nodes:      [{id, code, name, parent_id, sort_order}]
//   value:      currently selected id (number | string | '' | null)
//   onChange:   (newId) => void   — receives '' for unset
//   style:      optional override for the <select> style
//   placeholder: text for the empty option (default 'None')
//
// Leaf-only mode: the asset form only attaches to leaf nodes per the plan.
// Containers (nodes with children) are shown disabled — visible for context,
// but not pickable.
export default function FunctionalLocationPicker({ nodes = [], value = '', onChange, style, placeholder = 'None' }) {
  const ordered = useMemo(() => {
    const childrenByParent = new Map();
    for (const n of nodes) {
      const k = n.parent_id == null ? 0 : n.parent_id;
      if (!childrenByParent.has(k)) childrenByParent.set(k, []);
      childrenByParent.get(k).push(n);
    }
    for (const arr of childrenByParent.values()) {
      arr.sort((a, b) => (a.sort_order - b.sort_order) || a.code.localeCompare(b.code));
    }
    const out = [];
    const walk = (parentKey, depth) => {
      const kids = childrenByParent.get(parentKey) || [];
      for (const k of kids) {
        const hasChildren = (childrenByParent.get(k.id) || []).length > 0;
        out.push({ ...k, depth, hasChildren });
        walk(k.id, depth + 1);
      }
    };
    walk(0, 0);
    return out;
  }, [nodes]);

  return (
    <select
      value={value ?? ''}
      onChange={e => onChange?.(e.target.value)}
      style={style}>
      <option value="">{placeholder}</option>
      {ordered.map(n => (
        <option key={n.id} value={n.id} disabled={n.hasChildren}>
          {`${'  '.repeat(n.depth)}${n.code} — ${n.name}${n.hasChildren ? '  (container)' : ''}`}
        </option>
      ))}
    </select>
  );
}
